<script setup>
import { Handle, Position, useNode } from '@vue-flow/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const timeoutLabel = computed(() => {
  const val = props.data.timeout_value || 24;
  const unit = props.data.timeout_unit || 'hours';
  const unitLabel = unit === 'minutes' ? 'min' : unit === 'hours' ? 'h' : 'd';
  return `${val}${unitLabel}`;
});
</script>

<template>
  <div
    class="flow-node min-w-[160px] max-w-[220px] rounded-xl shadow-md border-2 transition-all"
    :class="selected ? 'border-slate-400' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="flex items-center gap-2 px-3 py-2 bg-slate-500 text-white rounded-t-xl">
      <span class="i-lucide-webhook size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || t('FLOW_BUILDER.NODES.WAIT_EXTERNAL_EVENT.TITLE') }}</span>
      <span v-if="audienceCount !== null" class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0">
        {{ audienceCount }}
      </span>
    </div>

    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs truncate">
      {{ t('FLOW_BUILDER.NODES.WAIT_EXTERNAL_EVENT.TIMEOUT', { value: timeoutLabel }) }}
    </div>

    <div class="relative bg-n-solid-1 rounded-b-xl pb-3 pt-1" :style="{ minHeight: '28px' }">
      <span class="absolute text-[9px] text-n-slate-9 text-center -translate-x-1/2" style="left: 25%; bottom: 10px;">{{ t('FLOW_BUILDER.NODES.WAIT_EXTERNAL_EVENT.RECEIVED') }}</span>
      <span class="absolute text-[9px] text-n-slate-9 text-center -translate-x-1/2" style="left: 75%; bottom: 10px;">{{ t('FLOW_BUILDER.NODES.WAIT_EXTERNAL_EVENT.TIMEOUT_LABEL') }}</span>
    </div>

    <Handle
      id="received"
      type="source"
      :position="Position.Bottom"
      class="!bg-slate-500 !border-slate-400"
      style="left: 25%"
    />
    <Handle
      id="timeout"
      type="source"
      :position="Position.Bottom"
      class="!bg-slate-400 !border-slate-300"
      style="left: 75%"
    />
  </div>
</template>
